import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import tokenService from '../../utils/tokenService';

const TokenManager = ({ children }) => {
    const navigate = useNavigate();

    useEffect(() => {
        // ตรวจสอบ session แล้วตัดสินใจว่าจะ logout หรือไม่
        // กฎสำคัญ: ลบ token ได้เฉพาะเมื่อ server "ยืนยัน" ว่า token ใช้ไม่ได้เท่านั้น
        // ถ้าเน็ตหลุด / timeout / server ล่ม → เก็บ token ไว้ ห้าม logout
        // (เดิมโค้ดนี้ลบ token ทุกกรณี ทำให้เน็ตสะดุดทีเดียวก็ต้องกรอกรหัสผ่านใหม่)
        const ensureSession = async (reason) => {
            if (!tokenService.getToken()) return;

            const status = await tokenService.checkSession();

            if (status === 'valid') {
                console.log(`✅ Token is valid (${reason})`);
                return;
            }

            if (status === 'unreachable') {
                console.log(`🌐 ตรวจสอบ session ไม่ได้ (${reason}) - เก็บ session ไว้ก่อน`);
                return;
            }

            console.log(`🔐 Invalid token (${reason}), attempting refresh...`);
            const refreshStatus = await tokenService.refreshSession(true);

            if (refreshStatus === 'unreachable') {
                console.log('🌐 ต่ออายุ session ไม่ได้เพราะเครือข่าย - เก็บ session ไว้ก่อน');
                return;
            }

            // session ตายจริง แต่ถ้าเครื่องนี้ถูก "จำไว้" ให้ต่อ session ใหม่เงียบ ๆ
            // ผู้ใช้จะไม่โดนเตะออกกลางทางและไม่ต้องกรอกรหัสผ่าน
            if (tokenService.getRememberToken()) {
                const rememberResult = await tokenService.loginWithRememberToken();
                if (rememberResult === 'ok') {
                    console.log('🔐 ต่อ session ใหม่จากอุปกรณ์ที่จำไว้สำเร็จ');
                    return;
                }
                if (rememberResult === 'unreachable') {
                    console.log('🌐 ต่อ session จากอุปกรณ์ที่จำไว้ไม่ได้เพราะเครือข่าย');
                    return;
                }
            }

            console.log('🔐 Session หมดอายุจริง - ออกจากระบบ (ยังจำชื่อผู้ใช้ไว้)');
            tokenService.clearToken({ keepProfile: true });
            // Don't redirect to login - allow guest access
        };

        // Initialize token manager on app start
        const initializeTokenManager = async () => {
            await ensureSession('app start');
        };

        initializeTokenManager();

        // Handle browser focus/visibility changes (mobile browsers)
        const handleVisibilityChange = async () => {
            if (!document.hidden && tokenService.isLoggedIn()) {
                console.log('📱 App became visible - checking token...');

                // Add a small delay to ensure app is fully loaded
                setTimeout(() => { ensureSession('visibility change'); }, 500);
            }
        };

        // Handle app going to background/foreground (mobile)
        const handlePageShow = async (event) => {
            if (event.persisted && tokenService.isLoggedIn()) {
                console.log('📱 Page restored from bfcache - refreshing token...');
                await tokenService.refreshToken(true);
            }
        };

        // Handle network reconnection
        const handleOnline = async () => {
            if (tokenService.isLoggedIn()) {
                console.log('🌐 Network reconnected - validating token...');
                await ensureSession('network reconnect');
            }
        };

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('online', handleOnline);

        // Cleanup
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('online', handleOnline);
        };
    }, [navigate]);

    // Enhanced error boundary for token-related errors
    useEffect(() => {
        // 401 จาก API ตัวใดตัวหนึ่ง "ไม่ได้" แปลว่า session ตาย
        // (อาจเป็นแค่ endpoint ที่ผู้ใช้ไม่มีสิทธิ์) จึงห้ามลบ token ทิ้งทันที
        // ให้ไปตรวจสอบกับ /auth/validate-token ก่อน แล้วค่อยตัดสินใจ
        const handleUnhandledRejection = (event) => {
            if (event.reason?.response?.status === 401 && tokenService.isLoggedIn()) {
                console.log('🔐 Unhandled 401 - verifying session before logging out');
                tokenService.refreshSession(true).then((status) => {
                    if (status === 'invalid') {
                        console.log('🔐 Session หมดอายุจริง - ออกจากระบบ (ยังจำชื่อผู้ใช้ไว้)');
                        tokenService.clearToken({ keepProfile: true });
                    }
                });
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        
        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, [navigate]);

    return children;
};

export default TokenManager;